/**
 * CAPA 1 — corte en origen.
 *
 * `Sentry.createReduxEnhancer({})` (la configuración que había) adjunta a CADA
 * evento el estado de Redux por TRES vías distintas:
 *
 *   1. `contexts.state.state.value` — el árbol completo, con profundidad
 *      `3 + normalizeDepth` (8 niveles con nuestro `normalizeDepth: 5`).
 *   2. `hint.attachments` — un `redux_state.json` con el árbol serializado
 *      entero, SIN límite de profundidad (`attachReduxState: true` por
 *      defecto). Los adjuntos no pasan por el data scrubbing de Sentry salvo
 *      que se active explícitamente en el proyecto.
 *   3. Un breadcrumb `redux.action` por acción, con el payload íntegro — o sea
 *      el array de conversaciones dentro de `GET_CONVERSATIONS_SUCCESS`.
 *
 * En el dashboard eso significa exportar la bandeja del asesor: teléfonos de
 * clientes, sus nombres, el texto de los mensajes y los ids de HubSpot/WhatsApp.
 *
 * Este módulo lo sustituye por un RESUMEN seguro por diseño: en vez de quitar
 * el contexto y quedarnos ciegos, mandamos la forma del estado (qué ducks hay,
 * cuántas conversaciones, si estaba cargando, qué rol tiene el usuario) que es
 * lo que de verdad sirve para diagnosticar, sin un solo dato de cliente.
 *
 * La garantía es estructural, no una lista de campos prohibidos:
 * **ningún string sale salvo que su clave esté en la allowlist.** El día que
 * alguien agregue `conversations[].clientRFC`, no sale — no hay que acordarse
 * de nada. Eso es lo que hace que el arreglo sobreviva al equipo.
 */

import * as Sentry from "@sentry/react";

import {
  isSensitiveKey,
  keyLooksLikePhone,
  normalizeKey,
  redactText,
  REDACTED,
  TRUNCATED,
} from "./piiRedaction";

/** Profundidad del resumen. Más allá emitimos solo los nombres de las claves. */
export const SUMMARY_MAX_DEPTH = 3;

/**
 * Tope de nodos por resumen.
 *
 * La profundidad sola no acota el tamaño: un mapa indexado por id con 800
 * entradas cabe entero en el nivel 2. @diegosiac lo midió con esa forma real —
 * 0.24 ms y 45 KB **por dispatch**, y el `stateTransformer` corre en CADA
 * acción de Redux, en el hilo principal del navegador del asesor. El tope
 * convierte ese coste en constante.
 *
 * 1 000 nodos es holgado para los 24 ducks del store y aun así deja el peor
 * caso un orden de magnitud por debajo de lo medido.
 */
export const SUMMARY_MAX_NODES = 1000;

/**
 * Únicas claves cuyo valor STRING puede viajar. Son identificadores técnicos y
 * enums — nunca contenido escrito por una persona. Todo lo demás se resume.
 */
const SAFE_STRING_KEYS = new Set([
  "id",
  "companyid",
  "userid",
  "agentid",
  "channelid",
  "spaceid",
  "conversationid",
  "selectedconversationid",
  "role",
  "status",
  "state",
  "step",
  "currentstep",
  "plan",
  "plantype",
  "source",
  "platform",
  "view",
  "tab",
  "locale",
  "lang",
]);

/** Máximo de nombres de clave que se enumeran en un `__keys`. */
export const SUMMARY_MAX_KEYS = 50;

/**
 * A partir de cuántos hijos dejamos de resumir hacia dentro y pasamos a
 * enumerar nombres. Separado de `SUMMARY_MAX_KEYS` a propósito: un objeto de
 * negocio con 60 campos (la company, p.ej.) sigue mereciendo su resumen
 * completo — sus `fetching`/`enabled` son diagnóstico; un mapa de 800 ids no.
 */
export const SUMMARY_MAX_FANOUT = 100;

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);

/**
 * Sanea el NOMBRE de una clave. Las claves también pueden ser el dato: un mapa
 * indexado por teléfono (`{ "5215544332211": {...} }`) fuga sin que ningún
 * valor sea sensible.
 *
 * `keyLooksLikePhone` solo cubre la clave que es un teléfono ENTERO —puros
 * dígitos y separadores—, así que un JID de WhatsApp (`5215544332211@c.us`)
 * pasaba verbatim por las dos capas: la forma que el comentario de `EMAIL_RE`
 * dice cubrir salía íntegra en `__keys`. Lo señaló @diegosiac en la revisión
 * del PR #230. Hoy no hay ningún mapa así en los ducks, pero la garantía que
 * vende este módulo es ESTRUCTURAL, y esta era la grieta.
 *
 * Pasarla por `redactText` la cierra sin enumerar formatos: cubre el JID, el
 * correo como clave y el teléfono incrustado en una clave compuesta.
 *
 * @param {string} k
 * @returns {string} clave saneada
 */
const safeKey = (k) => (keyLooksLikePhone(k) ? REDACTED : redactText(String(k)));

/**
 * Enumera claves con tope. Cuando hay más de las que caben, mandamos las
 * primeras y el TOTAL — el total es lo que de verdad se mira ("¿cuántas
 * conversaciones tenía cargadas?"), la lista completa nunca lo fue.
 *
 * @param {string[]} keys
 * @param {(k: string) => string} safeKey
 * @returns {{__keys: string[], __keyCount?: number}}
 */
function summarizeKeyList(keys, safeKey) {
  const out = { __keys: keys.slice(0, SUMMARY_MAX_KEYS).map(safeKey) };
  if (keys.length > SUMMARY_MAX_KEYS) out.__keyCount = keys.length;
  return out;
}

/**
 * Resume un valor cualquiera aplicando la regla "seguro por defecto".
 *
 * @param {*} value
 * @param {string} key   clave bajo la que cuelga (decide si un string sale)
 * @param {number} depth
 * @param {{n: number, max: number}} budget presupuesto de nodos, compartido
 *   por toda la travesía. Se agota una sola vez y se corta el resto.
 * @returns {*} versión resumida
 */
function summarizeValue(value, key, depth, budget) {
  budget.n += 1;
  if (budget.n > budget.max) return TRUNCATED;

  if (value === null || value === undefined) return value;

  // Booleans y números nunca son PII por sí solos, y son justo lo que sirve
  // para diagnosticar (`loading`, `fetching`, `hasMore`, contadores).
  if (typeof value === "boolean" || typeof value === "number") return value;

  if (typeof value === "string") {
    if (isSensitiveKey(key) || !SAFE_STRING_KEYS.has(normalizeKey(key))) {
      return "[Summarized]";
    }
    // Aun estando en la allowlist pasa por la capa 2: si alguien mete un
    // teléfono en un campo llamado `status`, tampoco sale.
    return redactText(value);
  }

  // Arrays: lo valioso para diagnóstico es CUÁNTOS, no cuáles.
  if (Array.isArray(value)) return { __count: value.length };

  if (value instanceof Date) return value.toISOString();

  if (isPlainObject(value)) {
    // Las CLAVES también pueden ser el dato — ver `safeKey`.
    const keys = Object.keys(value);

    // Dos cortes, no uno. La PROFUNDIDAD sola no acota el tamaño: un mapa
    // indexado por id con 800 entradas cabe entero en el nivel 2, y ahí es
    // donde @diegosiac midió 45 KB y 0,24 ms POR DISPATCH. Cuando un objeto
    // tiene más hijos de los que caben, mandamos los nombres y el TOTAL —
    // el total es lo que se mira de verdad; los 800 sub-resúmenes no.
    if (depth >= SUMMARY_MAX_DEPTH || keys.length > SUMMARY_MAX_FANOUT) {
      return summarizeKeyList(keys, safeKey);
    }
    const out = {};
    keys.forEach((childKey) => {
      // No omitimos las claves sensibles: seguimos resumiendo hacia dentro.
      // Sus strings caen igual por la allowlist (`lastMessage.text` →
      // `[Summarized]`), y así conservamos la forma, que es diagnóstico útil.
      out[safeKey(childKey)] = summarizeValue(value[childKey], childKey, depth + 1, budget);
    });
    return out;
  }

  // Funciones, símbolos, Map/Set y demás: solo el tipo.
  return `[${typeof value}]`;
}

/**
 * `stateTransformer` para el enhancer. Devuelve el resumen del árbol completo.
 *
 * @param {Object} state estado de Redux
 * @returns {Object|null} resumen, o `null` si el estado no es utilizable
 *   (`null` hace que Sentry no adjunte contexto de estado en absoluto).
 */
export function summarizeReduxState(state) {
  if (!isPlainObject(state)) return null;
  try {
    return summarizeValue(state, "__root", 0, { n: 0, max: SUMMARY_MAX_NODES });
  } catch (e) {
    // Nunca dejes que el resumen tire un dispatch: Redux quedaría roto para
    // el asesor. Ante la duda, no mandamos contexto.
    return { __error: "state summary failed" };
  }
}

/**
 * `actionTransformer` para el enhancer. La SECUENCIA de acciones es lo que
 * sirve para reconstruir qué hizo el asesor antes del error; el payload no.
 * Mandamos el `type` y la forma del payload, nunca sus valores.
 *
 * @param {Object} action acción de Redux
 * @returns {Object|null} acción resumida
 */
export function summarizeReduxAction(action) {
  if (!isPlainObject(action)) return null;
  try {
    const out = { type: redactText(String(action.type)) };

    if (Array.isArray(action.payload)) {
      out.payload = { __count: action.payload.length };
    } else if (isPlainObject(action.payload)) {
      const counts = {};
      Object.keys(action.payload).forEach((k) => {
        if (Array.isArray(action.payload[k])) counts[safeKey(k)] = action.payload[k].length;
      });
      out.payload = summarizeKeyList(Object.keys(action.payload), safeKey);
      if (Object.keys(counts).length > 0) out.payload.__counts = counts;
    }
    return out;
  } catch (e) {
    return { type: "[unserializable action]" };
  }
}

/**
 * Enhancer de Redux endurecido. Sustituye a `Sentry.createReduxEnhancer({})`.
 *
 * Diferencias con el default, todas deliberadas:
 *  - `attachReduxState: false` → sin `redux_state.json` adjunto. Era la vía
 *    que se saltaba tanto nuestro scrubber como el de Sentry.
 *  - `stateTransformer` → resumen en vez de árbol completo.
 *  - `actionTransformer` → `type` + forma del payload, sin valores.
 *
 * @returns {Function} enhancer para pasar a `createStore`
 */
export function createSafeReduxEnhancer() {
  return Sentry.createReduxEnhancer({
    attachReduxState: false,
    stateTransformer: summarizeReduxState,
    actionTransformer: summarizeReduxAction,
  });
}
